import { observable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import parseObjectToUrlParams from "../../../utils/parseObjectToUrlParams";
import TopFilter from "../common/TopFilter/TopFilter";
import TrackHistory from "../Dashboard/TrackHistory/TrackHistory";
import { ReportsStyled } from "./styled";
import AuthStore from "../../../stores/auth/auth.store";
import { t } from "i18next";
import { cn } from "../../../utils";
import moment from "moment";
import { DatePicker, Slider } from "antd";

const axios = require("axios");

interface Props extends RouteComponentProps<any> {
  authStore?: AuthStore;
}

function getSavingPotential(item: MixtureBehavior, displayTime: string) {
  if (displayTime === "day") {
    if (item.slump_deviation_day < 0) {
      return 0;
    }
    return Math.round(
      (item.slump_deviation_day / 25) * item.total_cube_day * 0.5
    );
  }

  if (displayTime === "week") {
    if (item.slump_deviation_week < 0) {
      return 0;
    }
    return Math.round(
      (item.slump_deviation_week / 25) * item.total_cube_week * 0.5
    );
  }

  if (displayTime === "month") {
    if (item.slump_deviation_month < 0) {
      return 0;
    }
    return Math.round(
      (item.slump_deviation_month / 25) * item.total_cube_month * 0.5
    );
  }
}
const dayColumns = [
  { title: t("Mixture name and code"), dataIndex: "mixture_name" },
  { title: t("Code-Id"), dataIndex: "mixture_code" },
  {
    title: t("mm for correction"),
    dataIndex: "slump_deviation_day",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "0";
    },
  },
  {
    title: t("Cubic Meter"),
    dataIndex: "total_cube_day",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "0";
    },
  },
  {
    title: t("Deliveries"),
    dataIndex: "number_of_deliveries_day",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "0";
    },
  },
  {
    title: t("Cost Savings Potential"),
    dataIndex: "flowCommand",
    render: (type: string, record: any) => {
      return getSavingPotential(record, "day") + " $";
    },
  },
];

const weekColumns = [
  { title: t("Mix design"), dataIndex: "mixture_name" },
  { title: t("Mix Design Code"), dataIndex: "mixture_code" },
  {
    title: t(" Slump correction in mm"),
    dataIndex: "slump_deviation_week",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("Cubic Meters"),
    dataIndex: "total_cube_week",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("deliveries"),
    dataIndex: "number_of_deliveries_week",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("Cost Savings Potential"),
    dataIndex: "flowCommand",
    render: (type: string, record: MixtureBehavior) => {
      return " $" + getSavingPotential(record, "week");
    },
  },
];
const monthColumns = [
  { title: t("Mixture name and code"), dataIndex: "mixture_name" },
  { title: t("Code-Id"), dataIndex: "mixture_code" },
  {
    title: t("mm for correction"),
    dataIndex: "slump_deviation_month",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("Cubic Meter"),
    dataIndex: "total_cube_month",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("Deliveries"),
    dataIndex: "number_of_deliveries_month",
    render: (value: string, record: any) => {
      return value && value !== null ? value : "-";
    },
  },
  {
    title: t("Cost Savings Potential"),
    dataIndex: "flowCommand",
    render: (type: string, record: MixtureBehavior) => {
      return getSavingPotential(record, "month") + " $";
    },
  },
];

function getDeviationRange(
  correction: string
): [number | null, number | null] | undefined {
  switch (correction) {
    case "-25mm":
      return [null, -25];
    case "-10mm":
      return [-24, -10];
    case "-9<mm<9":
      return [-9, 9];
    case "+10mm":
      return [10, 24];
    case "+25mm":
      return [25, null];
    default:
      return;
  }
}

type MixtureBehavior = {
  createdAt: string;
  date: string;
  factory_id: number;
  group_id: number;
  id: number;
  mixture_code: string;
  mixture_name: string;
  number_of_deliveries_day: number;
  number_of_deliveries_month: number;
  number_of_deliveries_week: number;
  slump_deviation_day: number;
  slump_deviation_month: number;
  slump_deviation_week: number;
  total_cube_day: number;
  total_cube_month: number;
  total_cube_week: number;
  updatedAt: string;
  report_status: string;
};

function isDeviationInRange(
  item: MixtureBehavior,
  displayTime: string,
  range: [number | null, number | null]
) {
  if (displayTime === "day") {
    return (
      (range[0] === null || item.slump_deviation_day >= range[0]) &&
      (range[1] === null || item.slump_deviation_day <= range[1])
    );
  }

  if (displayTime === "week") {
    return (
      (range[0] === null || item.slump_deviation_week >= range[0]) &&
      (range[1] === null || item.slump_deviation_week <= range[1])
    );
  }

  if (displayTime === "month") {
    return (
      (range[0] === null || item.slump_deviation_month >= range[0]) &&
      (range[1] === null || item.slump_deviation_month <= range[1])
    );
  }
}

async function fetchReports(
  group_id: number | string,
  filters: Record<string, any>
) {
  const rsp = await axios.get(
    `${process.env.REACT_APP_API_URL}/mixtures_behavior/calculate/${group_id}?` +
      parseObjectToUrlParams(filters)
  );

  return rsp.data.data;
}

@inject("authStore")
@observer
class Reports extends React.PureComponent<Props, any> {
  @observable data: MixtureBehavior[] = [];
  @observable sensor_number: string = "";
  @observable who: string;
  @observable type: string;
  @observable flow: any;
  @observable displayTime: "day" | "week" | "month" = "week";
  @observable savings = 0;
  @observable viewType: "chart" | "table" = "chart";
  @observable currentDate = moment().format("DD/MM/YYYY");
  @observable deviation:
    | "All"
    | "-25mm"
    | "-10mm"
    | "-9<mm<9"
    | "+10mm"
    | "+25mm" = "All";
  @observable filters: Record<string, any> = {};

  onCorrectionChange = (
    deviation: "All" | "-25mm" | "-10mm" | "-9<mm<9" | "+10mm" | "+25mm"
  ) => {
    this.deviation = deviation;
  };

  @observable tableHeader = [
    { label: t("id"), key: "id" },
    { label: t("sensorNumber"), key: "sensor_id" },
    { label: t("name"), key: "name" },
    { label: t("role"), key: "role" },

    { label: t("type"), key: "type" },
    { label: t("kamot"), key: "flow_command" },
    { label: t("realAdd"), key: "flow" },
    { label: t("date"), key: "time_stamp" },
  ];

  @observable group_id = this.props.authStore!.getUserGroipID();

  componentDidMount = async () => {
    await this.fetchData();
  };

  fetchData = async () => {
    try {
      // const current_date = this.data[0]?.date;
      // this.currentDate = moment(current_date).format("DD/MM/YYYY/");
      this.filters.date = this.currentDate;

      this.data = await fetchReports(this.group_id, this.filters);
      console.log({ da: this.data });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  onFilterChange = async (filterName: string, filterValue: any) => {
    switch (filterName) {
      case "factory_id":
        this.filters.factory_id = filterValue;
        break;

      case "loading_concreate_type":
        this.filters.mixture_code = filterValue;
        break;

      case "date":
        this.currentDate = filterValue;
        break;

      default:
        break;
    }
    this.fetchData();
  };

  @observable columns = [
    {
      title: t("Mixture name and code"),
      dataIndex: "mixture_name",
    },
    {
      title: t("Code-Id"),
      dataIndex: "mixture_code",
    },
    {
      title: t("mm for correction"),
      dataIndex: "slump_deviation_month",
      render: (value: string, record: any) => {
        return value && value !== null ? value : "-";
      },
    },
    {
      title: t("Cubic Meter"),
      dataIndex: "total_cube_week",
    },
    {
      title: t("Deliveries"),
      dataIndex: "createdAt",
      render: (type: string, record: any) => {
        return "0";
      },
    },
    {
      title: t("Cost Savings Potential"),
      dataIndex: "flowCommand",
      render: (type: string, record: any) => {
        let result: any = /[^_]*$/.exec(record!.flow_command)[0];
        result = result / 100;
        if (isNaN(result) || record.who === 1) {
          result = 0;
        }

        return result;
      },
    },
  ];

  @observable items = [
    { size: "All", name: "All" },
    { size: "-25mm", name: "-25mm" },
    { size: "-10mm", name: "-10mm" },
    { size: "-9<mm<9", name: "-9<mm<9" },
    { size: "+10mm", name: "+10mm" },
    { size: "+25mm", name: "+25mm" },
  ] as const;

  @observable time_selector = [
    { size: "day", name: "Day" },
    { size: "week", name: "Week" },
    { size: "month", name: "Month" },
  ] as const;

  getTotalSavings = (data: any[], displayTime: string): number => {
    return data
      .filter((item) => {
        const range = getDeviationRange(this.deviation);
        if (!range) return true;
        return isDeviationInRange(item, displayTime, range);
      })
      .filter((item) => {
        const saving = getSavingPotential(item, displayTime);
        return saving >= this.savings;
      })
      .reduce(
        (total, item) => total + getSavingPotential(item, displayTime),
        0
      );
  };

  createReport = async () => {
    const formattedDate = this.currentDate.replace(/\//g, "-");

    const url = `https://api.conextglobal.com/reports/mixtures_behavior/${this.filters.factory_id}/${formattedDate}/${this.displayTime}`;
    window.open(url, "_blank");
  };

  render() {
    return (
      <ReportsStyled>
        <div className="content-page">
          <div className="flex page-title justify-between w-full">
            <div className="flex ">
              <TopFilter
                customFilters={
                  <DatePicker
                    className="mb-6 rounded-[4px] border border-[#9CA3AF]"
                    onChange={(value) =>
                      this.onFilterChange(
                        "date",
                        moment(value).format("DD/MM/YYYY")
                      )
                    }
                    defaultValue={moment(this.currentDate, "DD/MM/YYYY")}
                    format="DD/MM/YYYY"
                  />
                }
                background={false}
                filters={["factory"]}
                onFilterChange={this.onFilterChange}
              />
              <div className="overflow-hidden pt-6">
                {this.time_selector.map((item) => (
                  <button
                    key={item.size}
                    className={cn(
                      "rounded-none bg-transparent text-black border-b-2 text-lg font-bold hover:text-slate-500 hover:border-slate-500 hover:border-b-4",
                      this.displayTime === item.size &&
                        "text-main-orange border-b-4 border-main-orange hover:text-main-orange hover:border-main-orange"
                    )}
                    onClick={() => {
                      if (this.displayTime === item.size) return;
                      this.onFilterChange("date-range", [
                        moment().subtract(1, item.size).format("YYYY-MM-DD"),
                        moment().format("YYYY-MM-DD"),
                      ]);
                      this.displayTime = item.size;
                    }}
                  >
                    {t(item.name)}
                  </button>
                ))}
              </div>
            </div>
            <div
              className="w-25 cursor-pointer pt-5"
              onClick={() => this.createReport()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                height="45"
                viewBox="0 0 142 175"
                width="45"
              >
                <g clip-path="url(#clip0)">
                  <path
                    d="M141.02 93.0761C141.2 109.353 141.445 125.636 141.528 141.916C141.559 147.861 141.102 153.808 141.057 159.755C140.995 168.12 134.466 173.177 126.874 173.334C111.609 173.649 96.3382 174.018 81.0725 173.935C62.5447 173.835 44.0176 173.357 25.4945 172.885C21.3465 172.675 17.214 172.227 13.1178 171.541C7.98585 170.81 2.93286 164.92 2.74778 159.328C2.28046 145.194 1.74544 131.056 1.58989 116.917C1.24728 85.8065 1.18042 54.6961 0.848309 23.5856C0.797771 18.8068 0.913967 14.1772 2.89152 9.75607C4.36893 6.44876 6.48497 3.76678 10.1519 2.77637C11.4512 2.43963 12.7838 2.24844 14.1254 2.20656C19.846 1.97947 25.5779 1.96199 31.2906 1.62201C51.053 0.445851 70.8113 0.879736 90.573 1.53804C93.3775 1.63124 96.1853 1.7625 98.9872 1.70277C100.916 1.67233 102.778 2.40762 104.164 3.74773C113.401 11.9471 122.634 20.1514 131.862 28.3604C133.204 29.5543 134.47 30.8434 135.885 31.9427C138.83 34.232 139.66 37.3445 139.716 40.8651C139.921 54.0017 140.182 67.137 140.384 80.2729C140.449 84.5391 140.394 88.8053 140.394 93.0715L141.02 93.0761ZM93.7129 9.8708C91.7747 9.71197 90.1188 9.48271 88.4622 9.45449C74.3181 9.21427 60.1735 8.99702 46.0286 8.80228C39.6345 8.71368 33.2392 8.67824 26.8445 8.63886C23.5956 8.61917 20.3396 8.51612 17.098 8.68086C12.4629 8.91649 11.1002 10.2599 10.2719 14.9494C9.4804 19.1462 9.31769 23.4378 9.78901 27.6826C10.0089 29.4652 10.1241 31.2589 10.1341 33.0551C10.2089 73.1353 10.2713 113.215 10.3212 153.292C10.2807 154.301 10.3179 155.312 10.4322 156.316C11.2375 161.436 13.9363 164.546 19.5211 164.771C41.1855 165.645 62.8499 165.641 84.5209 165.276C95.5224 165.09 106.525 165 117.527 164.875C120.218 164.845 122.917 164.951 125.6 164.806C130.02 164.568 132.356 162.145 132.307 157.704C132.252 152.772 131.887 147.843 131.805 142.911C131.709 137.187 131.904 131.456 131.739 125.735C131.335 111.486 130.64 97.2452 130.368 82.9948C130.146 71.3238 130.368 59.6441 130.379 47.9679C130.379 47.026 130.265 46.084 130.231 45.5655C122.735 45.5655 115.665 45.6188 108.597 45.5329C106.467 45.5299 104.344 45.2876 102.268 44.8108C98.0198 43.7784 96.7143 42.1496 96.2201 37.8433C96.0797 36.6186 96.0724 35.3779 95.9576 34.1492C95.233 26.2213 94.4966 18.2949 93.7129 9.8708ZM105.811 37.387C111.269 37.0772 116.413 36.7912 121.557 36.4887C122.768 36.4178 123.976 36.2915 125.916 36.1275L103.053 15.3782L102.534 15.7589C103.605 22.827 104.676 29.8955 105.811 37.387Z"
                    fill="black"
                  />
                  <path
                    d="M111.037 128.213C110.829 125.397 109.133 124.645 107.517 123.874C106.715 123.491 105.787 123.372 104.978 122.999C104.239 122.715 103.613 122.197 103.195 121.525C102.777 120.853 102.588 120.063 102.66 119.275C102.777 118.491 103.15 117.768 103.721 117.219C104.292 116.67 105.029 116.325 105.817 116.239C108.024 115.842 110.247 115.535 112.467 115.213C114.244 114.955 115.925 115.287 116.981 116.806C118.088 118.398 117.637 119.997 116.41 121.411C116.284 121.612 116.187 121.83 116.121 122.058C116.217 122.365 116.331 122.667 116.461 122.961C119.221 128.45 116.901 133.825 110.524 135.898C100.618 139.118 88.4417 133.372 89.0855 120.08C89.4905 111.728 93.4049 105.095 100.768 100.784C102.039 100.074 103.395 99.5268 104.803 99.1567C105.233 98.9827 105.695 98.9027 106.159 98.9217C106.623 98.9401 107.077 99.0582 107.492 99.2663C107.906 99.475 108.272 99.7697 108.563 100.131C108.855 100.492 109.066 100.911 109.183 101.36C109.455 102.377 108.694 103.07 107.624 103.586C105.687 104.451 103.862 105.547 102.189 106.851C100.272 108.425 98.7042 110.383 97.5864 112.598C96.4693 114.814 95.8261 117.237 95.6988 119.715C95.1823 127.02 101.458 131.993 108.224 129.741C109.206 129.319 110.148 128.808 111.037 128.213Z"
                    fill="black"
                  />
                  <path
                    d="M73.1272 125.606C74.5462 120.167 75.7526 115.529 76.9675 110.895C77.5089 108.834 78.0242 106.765 78.651 104.73C79.0087 103.551 79.4812 102.411 80.0608 101.324C80.8149 99.925 82.0252 99.1328 83.6838 99.5122C85.4034 99.906 85.9409 101.247 85.9547 102.818C86.0098 103.941 85.9193 105.066 85.6863 106.165C83.4731 114.742 81.2173 123.308 78.9188 131.866C78.6241 132.949 78.2079 133.996 77.6776 134.986C76.0368 138.067 72.7341 138.449 70.5734 135.72C69.3165 134.136 68.2342 132.421 67.3456 130.604C63.8697 123.443 60.4667 116.245 57.1369 109.011C56.5204 107.567 56.089 106.052 55.8531 104.5C55.6043 103.106 55.9719 101.792 57.4244 101.112C58.6833 100.521 60.1627 100.993 61.2469 102.421C61.832 103.244 62.3398 104.119 62.7637 105.035C65.8183 111.158 68.8624 117.286 71.8966 123.419C72.1394 123.906 72.4302 124.37 73.1272 125.606Z"
                    fill="black"
                  />
                  <path
                    d="M44.9895 103.998C39.4106 104.433 36.9737 106.509 36.2911 111.579C37.2533 112.043 38.3164 112.58 39.3994 113.074C41.438 114.004 43.5344 114.821 45.5218 115.849C47.9507 117.061 49.9767 118.95 51.3553 121.289C55.2054 127.898 51.27 135.764 43.6571 136.62C39.2597 137.115 35.5113 135.505 32.1161 132.888C31.6418 132.586 31.2824 132.134 31.0949 131.603C30.9459 130.677 30.7371 129.462 31.1933 128.823C31.6494 128.184 32.8669 127.959 33.7857 127.849C34.3837 127.777 35.038 128.264 35.6799 128.456C37.1651 128.959 38.6747 129.387 40.2028 129.738C43.1347 130.301 45.5192 129.393 46.4052 127.544C47.2453 125.788 46.1696 123.343 43.6414 121.647C42.413 120.868 41.1247 120.188 39.7886 119.613C38.0598 118.815 36.2346 118.215 34.5432 117.35C30.4903 115.276 29.183 111.604 31.0115 107.445C32.4555 104.164 34.6272 101.396 38.0303 100.089C39.793 99.4328 41.6879 99.2123 43.5541 99.4453C46.0121 99.7728 46.4242 101.387 44.9895 103.998Z"
                    fill="black"
                  />
                </g>
                <defs>
                  <clipPath id="clip0">
                    <rect
                      fill="white"
                      height="173.93"
                      transform="translate(0.706299 0.574219)"
                      width="141.113"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          <div className="title">Actions for Repair</div>

          <div className="flex justify-between items-center">
            <div className="flex flex-row items-center gap-14 p-2.5">
              <div className="flex flex-row items-center">
                {this.items.map((item) => (
                  <button
                    key={item.size}
                    className={cn(
                      "rounded-none py-px bg-transparent text-black border-b-2 border-black text-lg font-bold hover:text-slate-500 hover:border-slate-500 hover:border-b-4",
                      {
                        "text-main-orange border-b-4 border-main-orange hover:text-main-orange hover:border-main-orange py-0":
                          this.deviation === item.size,
                      }
                    )}
                    onClick={() => this.onCorrectionChange(item.size)}
                  >
                    {t(item.name)}
                  </button>
                ))}
              </div>
              <div className="w-full">
                <Slider
                  className="w-full min-w-44"
                  railStyle={{ backgroundColor: "#374151" }}
                  onChange={(value: number) => {
                    this.savings = value;
                  }}
                  marks={{
                    0: {
                      label: <span className="text-sm font-medium">0</span>,
                    },
                    50: {
                      label: <span className="text-sm font-medium">50</span>,
                    },
                    100: {
                      label: <span className="text-sm font-medium">100</span>,
                    },
                    150: {
                      label: <span className="text-sm font-medium">150</span>,
                    },
                  }}
                  min={0}
                  max={150}
                  tooltip={{
                    formatter: (value: number) => (
                      <div className="place-items-center ">
                        <div>Estimated Saving</div>
                        <div className="border px-2 py-1">{value}$</div>
                      </div>
                    ),
                  }}
                />
              </div>
            </div>
            <div className="flex gap5 align-center min-w-16">
              {this.viewType === "chart" ? (
                <img src="/images/chart-active.svg" alt="chart active" />
              ) : (
                <img
                  src="/images/chart-inactive.svg"
                  alt="chart"
                  className="pointer"
                  onClick={() => (this.viewType = "chart")}
                />
              )}
              <span
                style={{
                  backgroundColor: "#9CA3AF",
                  width: "1px",
                  height: "20px",
                }}
              ></span>
              {this.viewType === "table" ? (
                <img src="/images/table-active.svg" alt="table active" />
              ) : (
                <img
                  src="/images/table-inactive.svg"
                  className="pointer"
                  alt="table"
                  onClick={() => (this.viewType = "table")}
                />
              )}
            </div>
          </div>

          <div className="box">
            {this.displayTime === "day" && (
              <TrackHistory
                trackHistory={this.data
                  .filter((item) => {
                    const range = getDeviationRange(this.deviation);
                    if (!range) return true;
                    return isDeviationInRange(item, this.displayTime, range);
                  })
                  .filter((item) => {
                    const saving = getSavingPotential(item, this.displayTime);
                    return saving >= this.savings;
                  })
                  .sort(
                    (a, b) => a.slump_deviation_day - b.slump_deviation_day
                  )}
                columns={dayColumns}
              />
            )}
            {this.displayTime === "week" && (
              <TrackHistory
                trackHistory={this.data
                  .filter((item) => {
                    const range = getDeviationRange(this.deviation);
                    if (!range) return true;
                    return isDeviationInRange(item, this.displayTime, range);
                  })
                  .filter((item) => {
                    const saving = getSavingPotential(item, this.displayTime);
                    return saving >= this.savings;
                  })
                  .sort(
                    (a, b) => a.slump_deviation_week - b.slump_deviation_week
                  )}
                columns={weekColumns}
              />
            )}
            {this.displayTime === "month" && (
              <TrackHistory
                trackHistory={this.data
                  .filter((item) => {
                    const range = getDeviationRange(this.deviation);
                    if (!range) return true;
                    return isDeviationInRange(item, this.displayTime, range);
                  })
                  .filter((item) => {
                    const saving = getSavingPotential(item, this.displayTime);
                    return saving >= this.savings;
                  })
                  .sort(
                    (a, b) => a.slump_deviation_month - b.slump_deviation_month
                  )}
                columns={monthColumns}
              />
            )}
          </div>

          <div className="text-lg flex  justify-end gap-2 my-6">
            <span>{t("Total estimated savings")}:</span>
            <span className="font-semibold">
              {this.getTotalSavings(this.data, this.displayTime)} $
            </span>
          </div>
        </div>
      </ReportsStyled>
    );
  }
}
export default withRouter(Reports);
